import type { PresentationAuditEntry } from '../services/programPresentationAuditService';
import { canWriteSchoolScopedDataCache } from './schoolDataCacheVersion';
import { deviceCacheStorage } from './deviceCacheStorage';

const CACHE_VERSION = 'v1';
const ANNUAL_PREFIX = `cp.presentationAuditSnapshot.${CACHE_VERSION}.annual.`;
const PROGRAM_PREFIX = `cp.presentationAuditSnapshot.${CACHE_VERSION}.program.`;

export type CachedPresentationAuditSnapshot = {
  presentationEntries: PresentationAuditEntry[];
  scopedRowIds: string[];
  fixedRowIds: string[];
  programCellIds: string[];
  savedAt: number;
};

function annualCacheKey(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): string {
  return `${ANNUAL_PREFIX}${workspaceId}.${academicYear}.${termNumber}.${gradeId}`;
}

export function programPresentationAuditScopeSignature(params: {
  scopeType?: string;
  scopeKey?: string;
  includeFixedRows?: boolean;
  scheduleSlotKeysSignature: string;
}): string {
  return [
    params.scopeType ?? '',
    params.scopeKey ?? '',
    params.includeFixedRows ? '1' : '0',
    params.scheduleSlotKeysSignature,
  ].join('|');
}

function programCacheKey(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  scopeSignature: string
): string {
  return `${PROGRAM_PREFIX}${workspaceId}.${academicYear}.${termNumber}.${gradeId}.${scopeSignature}`;
}

function readCache(key: string): CachedPresentationAuditSnapshot | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = deviceCacheStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedPresentationAuditSnapshot;
    if (!parsed || !Array.isArray(parsed.presentationEntries)) return null;
    return {
      presentationEntries: parsed.presentationEntries,
      scopedRowIds: Array.isArray(parsed.scopedRowIds) ? parsed.scopedRowIds : [],
      fixedRowIds: Array.isArray(parsed.fixedRowIds) ? parsed.fixedRowIds : [],
      programCellIds: Array.isArray(parsed.programCellIds) ? parsed.programCellIds : [],
      savedAt: typeof parsed.savedAt === 'number' ? parsed.savedAt : Date.now(),
    };
  } catch {
    return null;
  }
}

function writeCache(key: string, workspaceId: string, snapshot: CachedPresentationAuditSnapshot): void {
  if (typeof window === 'undefined' || !canWriteSchoolScopedDataCache(workspaceId)) return;
  try {
    deviceCacheStorage.setItem(key, JSON.stringify(snapshot));
  } catch {
    /* ignore */
  }
}

function removeCache(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    deviceCacheStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export function loadAnnualPresentationAuditSnapshotCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): CachedPresentationAuditSnapshot | null {
  if (!workspaceId || !gradeId) return null;
  return readCache(annualCacheKey(workspaceId, academicYear, termNumber, gradeId));
}

export function saveAnnualPresentationAuditSnapshotCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  snapshot: Pick<CachedPresentationAuditSnapshot, 'presentationEntries' | 'scopedRowIds' | 'fixedRowIds' | 'programCellIds'>
): void {
  if (!workspaceId || !gradeId) return;
  writeCache(
    annualCacheKey(workspaceId, academicYear, termNumber, gradeId),
    workspaceId,
    { ...snapshot, savedAt: Date.now() }
  );
}

export function clearAnnualPresentationAuditSnapshotCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): void {
  if (!workspaceId || !gradeId) return;
  removeCache(annualCacheKey(workspaceId, academicYear, termNumber, gradeId));
}

export function loadProgramPresentationAuditSnapshotCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  scopeSignature: string
): CachedPresentationAuditSnapshot | null {
  if (!workspaceId || !gradeId) return null;
  return readCache(programCacheKey(workspaceId, academicYear, termNumber, gradeId, scopeSignature));
}

export function saveProgramPresentationAuditSnapshotCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  scopeSignature: string,
  snapshot: Pick<CachedPresentationAuditSnapshot, 'presentationEntries' | 'scopedRowIds' | 'fixedRowIds' | 'programCellIds'>
): void {
  if (!workspaceId || !gradeId) return;
  writeCache(
    programCacheKey(workspaceId, academicYear, termNumber, gradeId, scopeSignature),
    workspaceId,
    { ...snapshot, savedAt: Date.now() }
  );
}

export function clearProgramPresentationAuditSnapshotCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  scopeSignature: string
): void {
  if (!workspaceId || !gradeId) return;
  removeCache(programCacheKey(workspaceId, academicYear, termNumber, gradeId, scopeSignature));
}

export function clearAllPresentationAuditSnapshotCachesForWorkspace(workspaceId: string): void {
  if (!workspaceId || typeof window === 'undefined') return;
  const prefixes = [ANNUAL_PREFIX, PROGRAM_PREFIX];
  const keysToRemove: string[] = [];
  for (let i = 0; i < deviceCacheStorage.length; i += 1) {
    const key = deviceCacheStorage.key(i);
    if (!key) continue;
    if (prefixes.some((prefix) => key.startsWith(prefix) && key.includes(`.${workspaceId}.`))) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => deviceCacheStorage.removeItem(key));
}

export function clearPresentationAuditSnapshotCachesForGrade(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): void {
  if (!workspaceId || !gradeId || typeof window === 'undefined') return;
  const segment = `.${workspaceId}.${academicYear}.${termNumber}.${gradeId}`;
  const keysToRemove: string[] = [];
  for (let i = 0; i < deviceCacheStorage.length; i += 1) {
    const key = deviceCacheStorage.key(i);
    if (key && (key.startsWith(ANNUAL_PREFIX) || key.startsWith(PROGRAM_PREFIX)) && key.includes(segment)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => deviceCacheStorage.removeItem(key));
}
